<template>
  <!-- TODO clean mess -->
  <v-navigation-drawer
    v-model="show"
    v-lock-scroll="show"
    v-click-outside="clickOutside"
    class="budget-entity-panel"
    app
    stateless
    temporary
    right
    width="900px"
    ref="drawer"
  >
    <cg-alert v-if="budgetWasUpdating" type="warning" class="sticky-top">
      {{
        budgetIsStillUpdating
          ? 'This budget is still updating, it can take up to a few minutes'
          : 'Changes saved successfully! Refresh the page to see updates'
      }}
    </cg-alert>

    <!-- TODO debug ref drawer -->
    <close-panel-button v-if="!isAlertShow" @close="close()" />

    <unsaved-changes-alert
      @saveChanges="isInEditMode ? editBudgetEntity() : addNewBudgetEntity()"
      @closePanel="show = false"
      v-else
    >
      <template #default> You haven't saved your changes, exit without saving changes? </template>
    </unsaved-changes-alert>

    <v-container class="px-10">
      <v-row>
        <v-col cols="12" class="f30 lato-light">
          <panel-header :step="step" @update:step="handleStepChange" :show.sync="show">
            {{ headerTitle }}
          </panel-header>
        </v-col>
      </v-row>
    </v-container>

    <common-loader v-if="showLoader" />

    <v-container
      v-else-if="showTypeSelection"
      class="budget-entity-panel__type-selection py-10 px-10"
    >
      <div class="budget-entity-panel__heading">Select budget entity type:</div>
      <div class="budget-entity-panel__type-list">
        <div class="budget-entity-panel__type-item" @click="selectType('standard')">
          <icons-add-budget-type-standard class="budget-entity-panel__type-icon" />
          <div class="budget-entity-panel__type-title">Standard</div>
          <div class="budget-entity-panel__type-description">
            Control and allocate spend across your teams.
          </div>
        </div>
        <div class="budget-entity-panel__type-item" @click="selectType('recognition')">
          <icons-add-budget-type-recognition class="budget-entity-panel__type-icon" />
          <div class="budget-entity-panel__type-title">Recognition</div>
          <div class="budget-entity-panel__type-description">
            Reward employees with CG Credits. Let managers access the budget to recognize their teams.
          </div>
        </div>
      </div>
      <LearnMoreEntityTypes />
    </v-container>

    <v-container class="px-10" v-else>
      <v-row>
        <v-col cols="12">
          <v-divider class="my-6" />
        </v-col>
        <v-col cols="12">
          <v-row dense>
            <v-col
              cols="6"
              class="text-left d-flex flex-column"
              v-if="selectedEntity || isRecognitionBudgetType"
            >
              <label>{{ isRecognitionBudgetType ? 'Free balance' : 'Current balance' }}</label>
              <span class="f25 font-weight-regular">
                {{
                  (isRecognitionBudgetType ? initialBalance : form.balance)
                    | priceFilter({
                    style: 'currency',
                    currency: 'USD',
                  })
                }}
              </span>
            </v-col>
            <v-col
              :cols="selectedEntity || isRecognitionBudgetType ? 6 : 12"
              class="d-flex align-center text-right align-self-end"
              :class="{ 'justify-end': isRecognitionBudgetType || selectedEntity }"
              style="gap: 12px"
            >
              <common-switch
                v-model="form.active"
                :disabled="selectedEntity && form.general"
                id="status-checkbox"
              />
              <label for="status-checkbox" class="gray4 f14">
                <strong class="f15 black1">Status:</strong>
                {{ form.active ? 'Active' : 'Disabled' }}
              </label>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <label for="entity-name"> Budget entity name </label>
          <common-input
            v-model="form.name"
            :disabled="selectedEntity && form.general"
            :error-messages="errors.name"
            id="entity-name"
            clearable
            :hide-details="false"
          />
        </v-col>

        <v-col cols="12">
          <label for="description"> Budget entity description </label>
          <common-input
            v-model="form.description"
            :error-messages="errors.description"
            id="description"
            clearable
            :hide-details="false"
          />
        </v-col>

        <v-col v-if="userHasAccessToInventory && !isRecognitionBudgetType" cols="12">
          <common-checkbox v-model="form.is_inventory_only">
            For inventory shipping costs only
          </common-checkbox>
        </v-col>

        <v-col v-if="userIsCgElite" cols="12">
          <v-divider class="my-6" />
        </v-col>

        <v-col v-if="userIsCgElite" cols="12">
          <common-checkbox v-model="form.low_balance_alert_enabled">
            Enable low balance alert
          </common-checkbox>

          <div class="d-flex g10 align-center pt-5">
            Send alert email to:

            <select-multiple-entities
              id="alert-users"
              v-model="form.low_balance_recipients"
              :items="currentApproversForBalanceAlert"
              :disabled="!form.low_balance_alert_enabled"
              :badgeSuffix="form.low_balance_recipients.length > 1 ? 'approvers' : 'approver'"
              style="max-width: 200px"
              item-text="name"
              item-value="id"
              :height="36"
              badge
            />

            when balance drops to

            <common-input
              v-model.number="form.low_balance_alert_value"
              type="number"
              :disabled="!form.low_balance_alert_enabled"
              style="max-width: 100px"
              :height="36"
              prefix="$"
              @keydown="blockAmountSeparatorKeydown"
              @paste="blockAmountSeparatorPaste"
            />

            <common-tooltip top :max-width="280" :z-index="8" :activator-size="20">
              You will receive an email notifying you to reload CG Credits (you won't be charged)
            </common-tooltip>
          </div>
        </v-col>

        <template v-if="!userIsInGeneralGroup">
          <v-col cols="12" v-if="!isRecognitionBudgetType">
            <v-divider class="my-6" />
          </v-col>

          <v-col cols="12" v-if="!isRecognitionBudgetType">
            <label for="enable-order-limit"> Spend limit </label>
            <div class="d-flex align-center pt-10">
              <common-switch v-model="form.enable_order_limit" id="enable-order-limit" />
              <span class="gray4 ml-4" style="display: flex; gap: 8px; align-items: center">
                Enable spend limit per order

                <common-tooltip top :max-width="350" :z-index="8" :activator-size="20">
                  Set the max amount for an order paid from this budget entity
                </common-tooltip>
              </span>

              <span
                v-if="Array.isArray(errors.enable_order_limit) && errors.enable_order_limit.length"
                class="d-inline-block mt-2 red1"
                id="spend-limit-error"
              >
                {{ errors.enable_order_limit[0] }}
              </span>
            </div>
          </v-col>

          <v-col
            cols="12"
            class="d-flex align-center"
            v-if="form.enable_order_limit && !isRecognitionBudgetType"
          >
            <label for="order-limit" class="mr-2"> Max amount per order: </label>
            <!-- TODO fix global prepend inner icon... -->
            <common-input
              v-model="form.order_limit"
              id="order-limit"
              class="input-icon"
              height="30"
              prepend-inner-icon="$"
              style="max-width: 75px"
            />

            <span v-if="orderLimitError" class="red1">{{ orderLimitError }}</span>
          </v-col>

          <v-col cols="12" v-if="shouldShowAdmins || shouldShowApprovers">
            <v-divider class="my-6" />
          </v-col>

          <v-col v-if="shouldShowAdmins" cols="12">
            <admins
              :admins="adminApprovers"
              :selectedEntity="form"
              @selected="(selected) => updateAdmins(selected)"
            />
          </v-col>

          <v-col v-if="shouldShowApprovers" cols="12">
            <approvers
              :approvers="userApprovers"
              :selectedEntity="form"
              @selected="(selected) => updateApprovers(selected)"
            />
          </v-col>

          <v-col v-if="showApprovalFlowSelect" cols="12" id="approval-flow">
            <label for="approval-flow-select" class="d-block mb-2">Select approval flow</label>
            <common-select
              v-model="approvalFlowId"
              id="approval-flow-select"
              item-text="name"
              item-value="id"
              :items="approvalFlowItems"
              :disabled="approvalFlowItems.length === 1"
              :error="!!approvalFlowError"
              placeholder="Select approval flow"
              style="max-width: 360px"
            />
            <span v-if="approvalFlowError" class="red1">{{ approvalFlowError }}</span>
          </v-col>

          <v-col cols="12" class="d-flex align-center" v-if="!isRecognitionBudgetType">
            <label for="approval-limit" class="mr-2">
              Request approval for transactions above:
            </label>
            <common-input
              v-model="form.approval_limit"
              :disabled="!form.approvers.length"
              id="approval-limit"
              class="input-icon"
              height="30"
              prefix="$"
              style="max-width: 75px"
            />
          </v-col>

          <v-col cols="12">
            <v-divider class="my-6" />
          </v-col>

          <v-col cols="12" v-if="!isRecognitionBudgetType">
            <label for="balance-checkbox"> Show balance on checkout </label>

            <div class="d-flex align-center pt-6">
              <common-switch v-model="form.show_balance" id="balance-checkbox" />

              <div class="f15 gray4 ml-4" style="display: flex; gap: 8px">
                Display this budget entity balance for subaccounts on checkout

                <common-tooltip top :max-width="250" :z-index="8" :activator-size="20">
                  When checking out, users shall see the current balance of the budget entity
                </common-tooltip>
              </div>
            </div>
          </v-col>

          <v-col cols="12" v-if="!isRecognitionBudgetType">
            <v-divider class="my-6" />
          </v-col>

          <v-col cols="12">
            <customers
              :is-edit="isInEditMode"
              :selectedEntity="form"
              :accounts="userAccounts"
              :budgetEntities="budgetEntities"
              :budgetEntityId="form.id"
              ref="customers"
              :show-panel="show"
              @setAccounts="(selected) => (form.accounts = selected)"
              @balanceUpdated="updateRemainingBalance"
              :available-balance="form.balance"
              :initial-balance="initialBalance"
              :is-recognition-budget-type="isRecognitionBudgetType"
            />
          </v-col>
        </template>

        <v-col v-if="isRecognitionBudgetType" cols="12 mb-4">
          <common-checkbox v-model="form.personal_balance_notification">
            Notify users when they receive CG Credits to their personal balance
          </common-checkbox>
        </v-col>


        <v-col cols="12">
          <common-button
            @click="isInEditMode ? editBudgetEntity() : addNewBudgetEntity()"
            :disabled="!userHasChanges"
            :loading="addEditLoading"
            style="width: 160px"
            height="45"
          >
            Save changes
          </common-button>
        </v-col>

        <v-col cols="12" class="f15 lato-italic gray1 pb-10">
          By clicking “Save” you agree to the CorporateGift.com
          <a
            href="https://corporategift.com/terms-and-conditions/"
            target="_blank"
            class="f15 lato-italic green3"
          >
            Terms of Service and Payments Policy.
          </a>
          Prepaid credits are non-refundable and non-transferable.
        </v-col>
      </v-row>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import Api from '@/axios/api';
import { isEqual } from 'lodash';

import { CgAlert } from '@corporategiftcom/design-system';
import Approvers from '@/components/myCGCredits/budgetEntities/Approvers';
import Admins from '@/components/myCGCredits/budgetEntities/Admins';
import Customers from '@/components/myCGCredits/budgetEntities/Customers';
import UnsavedChangesAlert from '@/components/myInventory/Panels/common/UnsavedChangesAlert';
import SelectMultipleEntities from '@/components/mySubAccounts/SelectMultipleEntities.vue';

import panelVModel from '@/mixins/panelVModel';
import userIsCgElite from '@/mixins/userIsCgElite';
import superAdmin from '@/mixins/superAdmin'
import masterAccount from '@/mixins/masterAccount'
import { selectedEntity, budgetEntities } from '../mixins';
import PanelHeader from '@/components/PanelHeader.vue';
import LearnMoreEntityTypes from "@/components/myCGCredits/budgetEntities/LearnMoreEntityTypes.vue";
import blockAmountSeparatorKeydown, { blockAmountSeparatorPaste } from '@/utils/blockAmountSeparatorKeydown';

export default {
  name: 'AddEditBudgetEntity',
  components: {
    LearnMoreEntityTypes,
    PanelHeader,
    SelectMultipleEntities,
    UnsavedChangesAlert,
    Customers,
    Approvers,
    Admins,
    CgAlert,
  },
  mixins: [panelVModel, userIsCgElite, selectedEntity, budgetEntities, superAdmin, masterAccount],
  data: () => ({
    isAlertShow: false,
    loading: false,
    addEditLoading: false,
    budgetEntityIsStillUpdating: null,
    orderLimitError: '',
    form: {
      name: null,
      description: null,
      reload_limit: null,
      order_limit: null,
      approval_limit: null,
      color: null,
      active: true,
      general: false,
      balance: null,
      approvers: [],
      accounts: [],
      is_inventory_only: false,
      show_balance: true,
      low_balance_alert_enabled: false,
      low_balance_alert_value: null,
      low_balance_recipients: [],
      type: null,
      admin_ids: null,
      personal_balance_notification: false,
    },
    errors: {},
    userApprovers: [],
    adminApprovers: [],
    approvalFlows: [],
    // kept outside `form` - the list's `selectedEntity` has no approval flow, so it would always
    // register as an unsaved change in `userHasChanges`
    approvalFlowId: null,
    initialApprovalFlowId: null,
    approvalFlowError: '',
    userAccounts: [],
    initialBalance: null,
    step: 1,
  }),
  computed: {
    showTypeSelection() {
      return !this.selectedEntity && this.form.type === null;
    },
    isAdmin() {
      return this.superAdmin || this.masterAccount;
    },
    currentApproversForBalanceAlert() {
      return this.userApprovers;
    },
    headerTitle() {
      // 1. Edit mode
      if (this.selectedEntity) {
        return 'Edit budget entity details';
      }
      // 2. Choose type mode
      if (this.step === 1) {
        return 'Add new budget entity';
      }
      // 3. Type standard or recognition
      if (this.isStandardBudgetType) {
        return 'Add standard budget entity';
      }
      if (this.isRecognitionBudgetType) {
        return 'Add Recognition budget entity';
      }
      return 'Add new budget entity';
    },
    isRecognitionBudgetType() {
      return this.form.type === 'recognition';
    },
    isStandardBudgetType() {
      return this.form.type === 'standard';
    },
    hasAnyApprovers() {
      return this.userApprovers.length > 0 || this.adminApprovers.length > 0;
    },

    shouldShowAdmins() {
      if (!this.hasAnyApprovers) return false;

      if (this.isStandardBudgetType) return true;

      if (this.isRecognitionBudgetType) {
        return this.isAdmin;
      }

      return false;
    },
    shouldShowApprovers() {
      if (!this.hasAnyApprovers) return false;

      return this.isStandardBudgetType;
    },
    isInEditMode() {
      return !!this.selectedEntity || false;
    },
    selectedEntityId() {
      return this.selectedEntity?.id || null;
    },
    defaultEntity() {
      return this.selectedEntity || this.$options.data().form;
    },
    budgetWasUpdating() {
      return this.defaultEntity?.is_updating ?? false;
    },
    budgetIsStillUpdating() {
      const { budgetWasUpdating, budgetEntityIsStillUpdating } = this;

      if (budgetEntityIsStillUpdating !== null) {
        return budgetEntityIsStillUpdating;
      }
      return budgetWasUpdating;
    },
    userHasChanges() {
      const { budgetIsStillUpdating, defaultEntity, form } = this;

      if (budgetIsStillUpdating) {
        return false;
      }
      return (
        !isEqual(defaultEntity, Object.assign({}, form)) ||
        this.approvalFlowId !== this.initialApprovalFlowId
      );
    },
    approvalFlowItems() {
      return this.approvalFlows.map((flow) => ({
        id: flow.id,
        name: flow.name,
      }));
    },
    // same mechanism as the Add/Edit user panel: the flow is picked together with the approvers
    showApprovalFlowSelect() {
      return this.shouldShowApprovers && this.form.approvers.length > 0 && this.approvalFlows.length > 0;
    },
    userIsInGeneralGroup() {
      const companyId = this.$store.state.header?.headerData?.companyId ?? 1;
      return companyId === 1;
    },
    userHasAccessToInventory() {
      return this.$store.state.header?.headerData?.menus?.myInventory ?? false;
    },
    showLoader() {
      const { loading, budgetIsStillUpdating } = this;

      if (budgetIsStillUpdating) {
        return true;
      }
      return loading;
    },
  },
  watch: {
    show: function (val) {
      if (val) {
        this.fetchData();
        if (this.selectedEntity) {
          this.form = Object.assign({}, this.selectedEntity);
        }
      } else {
        Object.assign(this.$data, this.$options.data());
        // remove error messages from customers data table and clear editableCustomer object
        if (this.$refs.customers) {
          this.$refs.customers.editableCustomerErrors = [];
          this.$refs.customers.editableCustomer = {};
          this.$store.commit('budgetEntities/setErrors', {});
        }
      }
    },
    'form.approvers': function (val) {
      if (val?.length === 0 && this.form.approval_limit > 0) this.form.approval_limit = 0;
      if (val?.length === 0) this.approvalFlowId = null;
      this.preselectSingleApprovalFlow();
    },
    approvalFlows: 'preselectSingleApprovalFlow',
    approvalFlowId(val) {
      if (val) this.approvalFlowError = '';
      this.preselectSingleApprovalFlow();
    },
    errors: {
      deep: true,
      handler: 'scrollToFirstErrorInput',
    },
  },
  methods: {
    blockAmountSeparatorKeydown,
    blockAmountSeparatorPaste,
    selectType(type) {
      this.form.type = type;
      this.step = 2;
    },
    updateRemainingBalance(newBalance) {
      this.initialBalance = newBalance;
    },
    // with a single approval flow the select is disabled, so it's pre-selected
    preselectSingleApprovalFlow() {
      if (this.form.approvers?.length && this.approvalFlows.length === 1 && !this.approvalFlowId) {
        this.approvalFlowId = this.approvalFlows[0].id;
        // pre-selected while the panel loads - not a change made by the user
        if (this.loading) this.initialApprovalFlowId = this.approvalFlowId;
      }
    },
    validateApprovalFlow() {
      this.approvalFlowError =
        this.showApprovalFlowSelect && !this.approvalFlowId ? 'Select an approval flow' : '';
      if (this.approvalFlowError) {
        this.$el.querySelector('#approval-flow')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return !this.approvalFlowError;
    },
    buildPayload() {
      const { form, approvalFlowId } = this;
      return { ...form, approval_flow_id: form.approvers?.length ? approvalFlowId : null };
    },
    updateApprovers(selected) {
      if (this.isRecognitionBudgetType) {
        this.form.admin_ids = selected.map((a) => a.id);
      } else {
        this.form.approvers = selected;
      }
    },
    updateAdmins(selected) {
      this.form.admin_ids = selected.map((a) => a.id);
    },
    handleStepChange(val) {
      if (val === 1) {
        this.form.type = null;
      }
      this.step = val;
    },
    fetchData() {
      const { selectedEntityId } = this;

      this.loading = true;
      let fetchBudgetInfo = Promise.resolve([]);

      if (selectedEntityId) {
        fetchBudgetInfo = Api.get(`credit/budget-entities/${selectedEntityId}`).then(({ data }) => {
          this.budgetEntityIsStillUpdating = data.is_updating;
          this.form.type = data.type;
          this.initialBalance = data.recognition_balance;
          this.form.balance = data.balance;
          this.approvalFlowId = data.approval_flow_id ?? null;
          this.initialApprovalFlowId = this.approvalFlowId;
        });
      }

      const fetchApprovers = Api.get('/credit/budget-entities/approvers').then(
        ({ data }) => (this.userApprovers = data),
      );
      const fetchAdminApprovers = Api.get('/credit/budget-entities/admins').then(
        ({ data }) => (this.adminApprovers = data),
      );
      const fetchAccounts = Api.get('/credit/budget-entities/accounts').then(
        ({ data }) => (this.userAccounts = data),
      );
      const fetchApprovalFlows = Api.get('/credit/budget-entities/approval-flows').then(
        ({ data }) => (this.approvalFlows = data),
      );

      Promise.all([
        fetchApprovers,
        fetchAccounts,
        fetchBudgetInfo,
        fetchAdminApprovers,
        fetchApprovalFlows,
      ]).finally(() => (this.loading = false));
    },
    clickOutside(e) {
      if (e.target.className === 'v-overlay__scrim' && this.show) {
        this.close();
      }
    },
    async addNewBudgetEntity() {
      if (!this.validateApprovalFlow()) return;
      const form = this.buildPayload();
      this.addEditLoading = true;
      try {
        const { errors } = await Api.post('/credit/budget-entities', form);
        if (errors && Object.keys(errors).length) {
          this.errors = errors;
        } else {
          this.$cgToast.success('Changes saved successfully. Your updated values will appear once processing is complete.');
          this.show = false;
          this.$emit('fetchNewData');
        }
      } catch ({ response: { data: { errors, message } } }) {
        this.errors = errors || {};
        this.$cgToast.error(message || 'An error occurred, please contact our support');
      } finally {
        this.addEditLoading = false;
      }
    },
    async editBudgetEntity() {
      if (!this.validateApprovalFlow()) return;
      const form = this.buildPayload();
      this.addEditLoading = true;
      try {
        const { errors } = await Api.put(`/credit/budget-entities/${form.id}`, form);
        if (errors && Object.keys(errors).length) {
          this.errors = errors;
        } else {
          this.$cgToast.success('Changes saved successfully. Your updated values will appear once processing is complete.');
          this.show = false;
          this.$emit('fetchNewData');
        }
      } catch ({ response: { data: { errors, message } } }) {
        this.errors = errors || {};
        this.$cgToast.error(message || 'An error occurred, please contact our support');
      } finally {
        this.addEditLoading = false;
      }
    },
    close() {
      this.drawerScrollToTop();

      if (this.userHasChanges) {
        this.isAlertShow = true;
      } else {
        this.show = false;
      }
    },
    scrollToFirstErrorInput() {
      const { errors } = this;
      let requiredField = undefined;

      if (errors?.name) {
        requiredField = this.$el.querySelector('#entity-name');
      } else if (errors?.description) {
        requiredField = this.$el.querySelector('#description');
      } else if (errors?.enable_order_limit?.length) {
        requiredField = this.$el.querySelector('#spend-limit-error');
      }

      if (requiredField) {
        this.$el.querySelector('.v-navigation-drawer__content').scrollTo({
          top: requiredField.offsetTop,
          left: 0,
          behavior: 'smooth',
        });
      }
    },
    drawerScrollToTop() {
      this.$el.querySelector('.v-navigation-drawer__content').scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
  },
};
</script>

<style lang="scss">
@import 'src/css/add-edit-be';
</style>

<style lang="scss" scoped>
label {
  font-size: 15px;
  font-family: 'Lato-Bold', sans-serif;
  color: #000;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>
